// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppIcon,
  Button,
  Checkbox,
  DataTable,
  Dropdown,
  IconButton,
  StatTile,
  StatTileRow,
  TablePagination,
  TagFilterSelect,
  TextInput,
} from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { fundsApi, FundDTO, FundSearchResponse, FundStats } from '../api/fundsApi';
import { RiskRatingChip } from '../components/funds/RiskRatingChip';
import PortfolioListPanel from '../components/funds/PortfolioListPanel';
import { onCompanionFundSelect } from '../components/companion/companionFundSelect';
import { usePageContextRegister } from '../contexts/PageContextProvider';
import { useTags } from '../contexts/TagsContext';

/**
 * Advisor Fund Center — Investment Fund Centre listing (Figma Advisor 2.0
 * node 970-31108 + the stat-tile header from the Fund Center UI 2.0 PRD).
 *
 * Stat tiles → search-on-submit + filter pills → shared DataTable with
 * checkbox multi-select feeding the Portfolio Builder. Per-fund tag editing
 * lives on the Fund Details page; the tag FILTER stays here.
 */
export default function FundsPage() {
  usePageContextRegister({ pageType: 'FUNDS' });
  const navigate = useNavigate();

  // Search is submit-driven (Figma: input + Search button).
  const [searchDraft, setSearchDraft] = useState('');
  const [query, setQuery] = useState('');

  const [provider, setProvider] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('name');
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const { tags: allTags, featureEnabled: tagsFeatureEnabled } = useTags();
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([]);

  const [response, setResponse] = useState<FundSearchResponse | null>(null);
  const [stats, setStats] = useState<FundStats | null>(null);
  const [favouriteKeys, setFavouriteKeys] = useState<Set<string>>(new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fundsApi
      .search({
        q: query || undefined,
        provider: provider || undefined,
        category: category || undefined,
        sort,
        page,
        size,
        tagIds: tagFilterIds.length > 0 ? tagFilterIds : undefined,
      })
      .then((data) => {
        if (cancelled) return;
        setResponse(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load funds');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, provider, category, sort, page, size, tagFilterIds]);

  // Stat tiles are auxiliary: a failure shows em-dashes, never an error.
  useEffect(() => {
    let cancelled = false;
    fundsApi
      .getStats()
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fundsApi.listFavourites(0, 500).then((data) => {
      if (cancelled) return;
      setFavouriteKeys(new Set(data.content.map((f) => f.fundKey)));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleRows = useMemo(() => {
    if (!response) return [];
    return favouritesOnly
      ? response.content.filter((f) => favouriteKeys.has(f.fundKey))
      : response.content;
  }, [response, favouritesOnly, favouriteKeys]);

  /**
   * Chatbot link handler — `[name](fund:<key>)` links scroll the table to
   * the matching row anchor (`id="fund-row-<fundKey>"`, kept on the
   * fund-name cell content so the DataTable migration doesn't break it).
   */
  const handleChatbotFundSelect = useCallback((fundKey: string) => {
    const el = document.getElementById(`fund-row-${fundKey}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('fund-row-pulse');
      window.setTimeout(() => el.classList.remove('fund-row-pulse'), 1500);
    }
  }, []);

  useEffect(() => onCompanionFundSelect(handleChatbotFundSelect), [handleChatbotFundSelect]);

  const toggleFavourite = async (fund: FundDTO) => {
    const next = new Set(favouriteKeys);
    if (next.has(fund.fundKey)) {
      next.delete(fund.fundKey);
      setFavouriteKeys(next);
      try {
        await fundsApi.removeFavourite(fund.fundKey);
      } catch {
        next.add(fund.fundKey);
        setFavouriteKeys(new Set(next));
      }
    } else {
      next.add(fund.fundKey);
      setFavouriteKeys(next);
      try {
        await fundsApi.addFavourite(fund.fundKey);
      } catch {
        next.delete(fund.fundKey);
        setFavouriteKeys(new Set(next));
      }
    }
  };

  const toggleSelected = (fundKey: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(fundKey)) {
        next.delete(fundKey);
      } else {
        next.add(fundKey);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setSearchDraft('');
    setQuery('');
    setProvider('');
    setCategory('');
    setSort('name');
    setFavouritesOnly(false);
    setTagFilterIds([]);
    setPage(0);
  };

  const openPortfolioBuilder = () => {
    navigate('/funds/portfolios/new', { state: { fundKeys: Array.from(selectedKeys) } });
  };

  const providerOptions = useMemo(() => {
    if (!response) return [];
    const codes = new Set<string>();
    response.content.forEach((f) => f.providerCode && codes.add(f.providerCode));
    return Array.from(codes).sort().map((c) => ({ value: c, text: c }));
  }, [response]);

  const categoryOptions = useMemo(() => {
    if (!response) return [];
    const cats = new Set<string>();
    response.content.forEach((f) => f.categoryName && cats.add(f.categoryName));
    return Array.from(cats).sort().map((c) => ({ value: c, text: c }));
  }, [response]);

  const currentYear = String(new Date().getFullYear());

  const columns: DataTableColumn<FundDTO>[] = [
    {
      key: 'select',
      header: '',
      width: '80px',
      render: (fund) => {
        const isFav = favouriteKeys.has(fund.fundKey);
        return (
          <span
            className="flex items-center gap-xs"
            onClick={(e) => e.stopPropagation()}
            data-testid={`fund-select-${fund.fundKey}`}
          >
            <Checkbox
              size="sm"
              checked={selectedKeys.has(fund.fundKey)}
              onChange={() => toggleSelected(fund.fundKey)}
              aria-label={`Select ${fund.fundName}`}
            />
            <IconButton
              variant="ghost"
              aria-label={isFav ? 'Remove favourite' : 'Add favourite'}
              onClick={() => toggleFavourite(fund)}
              icon={<AppIcon name="heart" size={18} variant={isFav ? 'Bold' : 'Linear'} />}
            />
          </span>
        );
      },
    },
    {
      key: 'fund',
      header: 'Fund Name',
      render: (fund) => (
        <div id={`fund-row-${fund.fundKey}`}>
          <div className="font-medium">{fund.fundName}</div>
          <div className="text-body-04 text-grey-03">{fund.fundKey}</div>
        </div>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (fund) => fund.providerDisplayName || fund.providerCode,
    },
    {
      key: 'category',
      header: 'Category',
      render: (fund) => fund.categoryName || '—',
    },
    {
      key: 'risk',
      header: 'Risk',
      width: '80px',
      render: (fund) => <RiskRatingChip rating={fund.riskRating} raw={fund.riskRatingRaw} />,
    },
    {
      key: 'price',
      header: 'Price',
      width: '90px',
      render: (fund) => (fund.price != null ? fund.price.toFixed(4) : '—'),
    },
    {
      key: 'ytd',
      header: 'YTD',
      width: '80px',
      render: (fund) => formatPctString(fund.calendarYears?.[currentYear]),
    },
    {
      key: 'perf1y',
      header: '1 Year',
      width: '80px',
      render: (fund) => formatPct(fund.perf1yPct),
    },
    {
      key: 'perf3y',
      header: '3 Years',
      width: '80px',
      render: (fund) => formatPct(fund.perf3yPct),
    },
    {
      key: 'perf5y',
      header: '5 Years',
      width: '80px',
      render: (fund) => formatPct(fund.perf5yPct),
    },
    {
      key: 'perf10y',
      header: '10 Years',
      width: '85px',
      render: (fund) => formatPct(fund.perf10yPct),
    },
    {
      key: 'priceDate',
      header: 'Price Date',
      width: '110px',
      render: (fund) => (fund.priceDate ? formatDate(fund.priceDate) : '—'),
    },
    {
      key: 'factsheet',
      header: 'Factsheet',
      width: '90px',
      render: (fund) =>
        fund.factsheetUrl ? (
          <a
            href={fund.factsheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Open
          </a>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div className="mx-lg my-lg flex flex-col gap-lg" data-testid="funds-page">
      <StatTileRow data-testid="funds-stat-tiles">
        <StatTile label="Funds" value={stats ? stats.totalFunds.toLocaleString() : null} />
        <StatTile label="Providers" value={stats?.providerCount ?? null} />
        <StatTile label="Favourites" value={stats?.favouriteCount ?? null} />
        <StatTile label="Portfolios" value={stats?.portfolioCount ?? null} />
        <StatTile
          label="Last updated"
          value={stats?.lastSyncedAt ? formatDate(stats.lastSyncedAt) : null}
        />
      </StatTileRow>

      <PortfolioListPanel
        headerRight={
          <Button size="md" onClick={openPortfolioBuilder} data-testid="portfolio-builder-button">
            Portfolio Builder{selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ''}
          </Button>
        }
      />

      <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-lg">
        <h1 className="m-0 text-body-01 font-medium">Investment funds</h1>

        <div className="flex items-end gap-md flex-wrap">
          <form
            className="flex items-end gap-sm flex-1 min-w-0"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(searchDraft);
              setPage(0);
            }}
          >
            <div className="flex-1">
              <TextInput
                placeholder="Search by fund name, provider or category"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                aria-label="Search funds"
              />
            </div>
            <Button type="submit" variant="outlined" size="md">
              Search
            </Button>
          </form>
        </div>

        <div className="flex items-center gap-sm flex-wrap">
          <Button
            variant={favouritesOnly ? 'filled' : 'outlined'}
            size="sm"
            onClick={() => setFavouritesOnly((v) => !v)}
            iconRight={<AppIcon name="heart" size={16} variant={favouritesOnly ? 'Bold' : 'Linear'} />}
          >
            Favourites
          </Button>
          <Dropdown
            value={provider}
            options={[{ value: '', text: 'All providers' }, ...providerOptions]}
            onChange={(v) => {
              setProvider(v);
              setPage(0);
            }}
            aria-label="Provider filter"
          />
          <Dropdown
            value={category}
            options={[{ value: '', text: 'All categories' }, ...categoryOptions]}
            onChange={(v) => {
              setCategory(v);
              setPage(0);
            }}
            aria-label="Category filter"
          />
          <Dropdown
            value={sort}
            options={[
              { value: 'name', text: 'Name (A–Z)' },
              { value: 'name_desc', text: 'Name (Z–A)' },
              { value: 'perf1y_desc', text: '1y performance (high → low)' },
              { value: 'perf1y_asc', text: '1y performance (low → high)' },
              { value: 'perf3y_desc', text: '3y performance (high → low)' },
              { value: 'risk_asc', text: 'Risk (low → high)' },
              { value: 'risk_desc', text: 'Risk (high → low)' },
              { value: 'provider', text: 'Provider' },
            ]}
            onChange={(v) => {
              setSort(v);
              setPage(0);
            }}
            aria-label="Sort"
          />
          {tagsFeatureEnabled && (
            <TagFilterSelect
              options={allTags.filter((t) => !t.archived).map((t) => ({ id: t.id, name: t.name, color: t.color }))}
              selectedIds={tagFilterIds}
              onChange={(next) => {
                setTagFilterIds(next);
                setPage(0);
              }}
              data-testid="funds-tag-filter"
            />
          )}
          <Button variant="link" size="sm" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <DataTable
          columns={columns}
          data={visibleRows}
          rowKey={(fund) => fund.fundKey}
          onRowClick={(fund) => navigate(`/funds/${encodeURIComponent(fund.fundKey)}`)}
          loading={loading}
          skeletonRows={8}
          emptyState={<span>No funds match your filters.</span>}
        />

        {response && (
          <TablePagination
            config={{
              currentPage: response.page + 1,
              totalPages: response.totalPages,
              itemsPerPage: response.size,
              itemsPerPageOptions: [10, 25, 50],
              onPageChange: (p) => setPage(p - 1),
              onItemsPerPageChange: (s) => {
                setSize(s);
                setPage(0);
              },
            }}
          />
        )}
      </div>
    </div>
  );
}

function formatPct(value: number | null): string {
  if (value == null) return '—';
  const formatted = value.toFixed(2);
  return value > 0 ? `+${formatted}%` : `${formatted}%`;
}

/** Calendar-year figures arrive as provider-formatted strings (e.g. "13.6"). */
function formatPctString(value: string | undefined): string {
  if (value == null || value === '') return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? formatPct(parsed) : value;
}
